import { useEffect, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useCasca } from '@/pages/admin/configuracoes/casca-contrato';

/**
 * PageHeader — cabeçalho canônico de página (FASE-B design system).
 *
 * TODA tela tem um h1 (14 telas não tinham nenhum; 15 repetiam
 * "Configurações" genérico — dor 9 do diagnóstico). Anatomia da maquete:
 *   crumb    — Inter 600 12px caps (ink-3), opcional (ex.: "Comercial")
 *   title    — h1 Oswald 500 36px (28px no mobile)
 *   subtitle — Inter 400 14px (ink-2), opcional
 *   actions  — botões à direita, empilham no mobile
 *
 * <PageHeader crumb="Comercial" title="Funil de negócios"
 *   subtitle="8 propostas ativas" actions={<Button>Nova proposta</Button>} />
 *
 * ── DENTRO da casca da Configurações, ele se cala (K-4, 02/08)
 *
 * Quando a tela é renderizada no painel da Configurações, a casca já disse onde
 * a pessoa está — breadcrumb e título são dela. Um segundo cabeçalho aqui produz
 * o que o smoke encontrou em `/admin/configuracoes/catalogos/tags`: a palavra
 * "Tags" três vezes e dois breadcrumbs se contradizendo.
 *
 * Então, dentro da casca, este componente **não desenha nada** — e **publica as
 * `actions` no slot do cabeçalho da casca**, que as mostra à direita, no mesmo
 * lugar em todas as telas.
 *
 * A tela filha não muda uma linha: continua passando `crumb`, `title` e
 * `actions` como sempre. Quem decide o que fazer com isso é este componente, num
 * lugar só — e não 14 telas, cada uma do seu jeito.
 */
interface PageHeaderProps {
  /**
   * Marca ESTE cabeçalho como o da própria casca da Configurações (K-4).
   *
   * Existe porque o `CascaProvider` envolve a casca inteira — inclusive o
   * cabeçalho dela. Sem esta marca, o silenciamento se aplicaria também ao
   * cabeçalho que deveria sobrar, e a página ficaria SEM título nenhum: foi
   * exatamente o que aconteceu na primeira versão.
   *
   * Só a casca usa. Tela de conteúdo nunca passa isto.
   */
  daCasca?: boolean;
  crumb?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function PageHeader({ daCasca = false, crumb, title, subtitle, actions, className, ...props }: PageHeaderProps) {
  const { dentroDaCasca, publicarAcoes } = useCasca();
  const silenciar = dentroDaCasca && !daCasca;

  // Publica a ação no slot da casca (e limpa ao sair, senão o botão da tela
  // anterior sobreviveria à navegação — um "Nova tag" pendurado na tela de
  // Departamentos).
  useEffect(() => {
    if (!silenciar) return;
    publicarAcoes(actions ?? null);
    return () => publicarAcoes(null);
  }, [silenciar, actions, publicarAcoes]);

  if (silenciar) return null;

  return (
    <div className={cn('mb-6 flex flex-wrap items-start gap-x-4 gap-y-3', className)} {...props}>
      <div className="min-w-0 flex-1">
        {crumb && (
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
            {crumb}
          </div>
        )}
        <h1 className="font-display text-[28px] font-medium leading-tight tracking-tight text-ink-1 dark:text-ink-dark-1 md:text-4xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1.5 max-w-[56ch] text-sm text-ink-2 dark:text-ink-dark-2">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
